@if (open && bot) {
<div class="fixed inset-0 z-[10050] flex items-end justify-end p-3 sm:p-5" role="dialog" aria-modal="true"
  [attr.aria-label]="'Chat: ' + bot.name">
  <div class="absolute inset-0 bg-[#0f0f1a]/10 backdrop-blur-sm dark:bg-black/10" @backdropFade
    (click)="onBackdropClick()"></div>

  <div
    class="relative z-10 flex h-[min(88vh,40rem)] w-full max-w-[30rem] flex-col overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-[0_24px_80px_-20px_rgba(80,60,120,0.45)] dark:border-line dark:bg-surface-0 dark:shadow-[0_24px_80px_-20px_rgba(0,0,0,0.65)]"
    @panelFromCorner (click)="$event.stopPropagation()">
    <header
      class="flex shrink-0 items-center gap-3 border-b border-gray-200/90 bg-gradient-to-r from-[#faf8ff] to-white px-4 py-3.5 dark:border-line dark:from-surface-0 dark:to-surface-0">
      <div
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-500/15 ring-1 ring-purple-500/25 dark:bg-purple-500/20 dark:ring-purple-400/30">
        <app-icon-v2 name="bot-message-square" [size]="22" class="text-purple-600 dark:text-purple-300" />
      </div>
      <div class="min-w-0 flex-1">
        <p class="truncate text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {{ bot.productName }}
        </p>
        <h2 class="truncate text-base font-semibold text-[#141437] dark:text-white">
          {{ bot.name }}
        </h2>
      </div>
      <button type="button"
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition hover:bg-gray-50 dark:border-gray-600 dark:bg-surface-0 dark:text-gray-300 dark:hover:bg-gray-800"
        (click)="close()" aria-label="Close chat">
        <app-icon-v2 name="x" [size]="18" />
      </button>
    </header>

    <!-- Messages -->
    <div #messageScroll
      class="chat-workspace-scroll flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overflow-x-hidden bg-gradient-to-b from-gray-50/90 to-white px-3 py-4 dark:from-surface-0/60 dark:to-surface-0"
      role="log" aria-live="polite" aria-relevant="additions">
      @for (msg of messages; track trackMessage($index, msg)) {
      @if (msg.role === 'assistant') {
      <div class="flex w-full justify-start gap-2">
        <div
          class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-purple-500/12 ring-1 ring-purple-500/20 dark:bg-purple-500/18 dark:ring-purple-400/25">
          <app-icon-v2 name="sparkles" [size]="16" class="text-purple-600 dark:text-purple-300" />
        </div>
        <div
          class="max-w-[min(100%,18rem)] rounded-2xl rounded-tl-md border border-gray-200/90 bg-white/95 px-3.5 py-2.5 text-[13px] leading-relaxed text-[#2d2d4a] shadow-sm dark:border-line dark:bg-surface-1 dark:text-ink-soft">
          @if (isLastAssistantStreaming($index)) {
          <span class="inline-flex gap-1 text-gray-400" aria-hidden="true">
            <span class="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:0ms]"></span>
            <span class="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:150ms]"></span>
            <span class="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:300ms]"></span>
          </span>
          } @else {
          <span class="whitespace-pre-wrap break-words">{{ msg.text }}</span>
          }
        </div>
      </div>
      } @else {
      <div class="flex w-full justify-end">
        <div
          class="max-w-[min(100%,18rem)] rounded-2xl rounded-tr-md bg-gradient-to-br from-[#7c3aed] via-[#6d28d9] to-[#5b21b6] px-3.5 py-2.5 text-[13px] leading-relaxed text-white shadow-md shadow-purple-900/20">
          <p class="whitespace-pre-wrap break-words">{{ msg.text }}</p>
        </div>
      </div>
      }
      }
    </div>

    <!-- Quick prompts (input sets) -->
    @if (showQuickPrompts && quickPrompts.length > 0) {
    <div class="shrink-0 border-t border-gray-200/70 bg-white/60 px-3 pb-2 pt-2 dark:border-line dark:bg-surface-0/80">
      <p class="mb-2 px-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        Try asking
      </p>
      <div class="flex flex-wrap gap-2">
        @for (prompt of quickPrompts; track prompt) {
        <button type="button" (click)="sendFromPrompt(prompt)" [disabled]="isSending"
          class="inline-flex max-w-full items-center gap-1.5 rounded-full border border-purple-200/80 bg-gradient-to-b from-white to-purple-50/80 px-3 py-1.5 text-left text-[12px] font-medium text-[#4c1d95] shadow-sm transition hover:border-purple-300 hover:shadow-md active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 dark:border-purple-500/35 dark:from-surface-2 dark:to-surface-0 dark:text-purple-200/95 dark:hover:border-line-strong">
          <app-icon-v2 name="corner-down-right" [size]="13" class="shrink-0 opacity-70" />
          <span class="line-clamp-2">{{ prompt }}</span>
        </button>
        }
      </div>
    </div>
    }

    <!-- Composer -->
    <div
      class="shrink-0 border-t border-gray-200/90 bg-white px-3 pb-3 pt-2 dark:border-line dark:bg-surface-0">
      <div
        class="flex items-end gap-2 rounded-2xl border border-gray-200/90 bg-gray-50/90 px-2 py-1.5 shadow-inner dark:border-line dark:bg-surface-0/90 focus-within:border-purple-400/60 focus-within:ring-2 focus-within:ring-purple-500/15 dark:focus-within:border-purple-500/40">
        <input #sessionDocumentInput type="file" multiple class="hidden" accept=".pdf,.doc,.docx,.txt,.csv,.json"
          (change)="uploadSessionDocuments($event)" />
        <button type="button" (click)="sessionDocumentInput.click()"
          class="mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-purple-200/80 bg-white text-purple-700 shadow-sm transition hover:border-purple-300 hover:bg-purple-50 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-40 dark:border-purple-500/35 dark:bg-surface-2 dark:text-purple-200 dark:hover:border-line-strong"
          [disabled]="isSending || isUploadingDocument" aria-label="Upload document">
          <app-icon-v2 name="upload" [size]="18" />
        </button>
        <textarea #composer rows="1" [placeholder]="inputPlaceholder" [(ngModel)]="draft"
          [disabled]="isSending"
          (keydown)="onComposerKeydown($event)" (input)="adjustComposerHeight($event)"
          class="max-h-[7.5rem] min-h-[2.5rem] flex-1 resize-none bg-transparent px-2 py-2 text-[13px] leading-snug text-[#1e1b4b] placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 dark:text-ink-soft dark:placeholder:text-gray-500"
          [attr.aria-label]="inputPlaceholder"></textarea>
        <button type="button" (click)="send()"
          class="mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 text-white shadow-md transition hover:from-purple-500 hover:to-indigo-500 hover:shadow-lg active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
          [disabled]="!draft.trim() || isSending" aria-label="Send message">
          <app-icon-v2 name="send" [size]="18" />
        </button>
      </div>
      <p class="mt-1.5 px-1 text-center text-[10px] text-gray-400 dark:text-gray-500">
        Enter to send · Shift+Enter for a new line
      </p>
    </div>
  </div>
</div>
}
